import { motionPresets } from "./motionPresets";
import { getRecipeMetadata, getSceneRecipeMapForFamily } from "./sceneRecipeRegistry";

function normalizeRole(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function getElementRole(element = {}) {
  return normalizeRole(
    element.role ||
      element.asset_binding?.asset_role ||
      element.asset_binding?.asset_key ||
      element.id ||
      ""
  );
}

function hasRoleLike(elements = [], tokens = []) {
  return elements.some((element) => {
    const role = getElementRole(element);
    return tokens.some((token) => role.includes(token));
  });
}

function findSelectedAssetByRole(selectedAssets = [], tokens = []) {
  return selectedAssets.find((asset) => {
    const role = normalizeRole(asset.asset_role || asset.role || asset.asset_key || asset.name || "");
    return tokens.some((token) => role.includes(token));
  });
}

function buildRecipeFigure({
  id,
  role,
  layerIndex,
  placement,
  fill,
  stroke = "rgba(212, 175, 55, 0.45)",
  strokeWidth = 0,
  cornerRadius = 0,
  opacity = 1,
  animation = [],
  subType = "rect",
  recipeId,
}) {
  return {
    id,
    role,
    type: "figure",
    layer_index: layerIndex,
    placement,
    opacity,
    rotation: 0,
    editable: true,
    animation,
    figure: {
      sub_type: subType,
      fill,
      stroke,
      stroke_width: strokeWidth,
      corner_radius: cornerRadius,
    },
    custom: {
      enrichment_source: "scene_recipe",
      recipe_id: recipeId,
    },
  };
}

function buildRecipeAsset({
  id,
  role,
  asset,
  layerIndex,
  placement,
  opacity = 1,
  animation = [],
  recipeId,
}) {
  const mediaUrl = asset?.media_url || asset?.file_url || asset?.preview_url || asset?.thumbnail_url || "";

  return {
    id,
    role,
    type: "image",
    layer_index: layerIndex,
    asset_binding: {
      asset_role: asset?.asset_role || asset?.role || role,
      asset_key: asset?.asset_key || asset?.asset_role || role,
      source_strategy: mediaUrl ? "use_library_asset" : "placeholder_until_available",
      asset_library_item_id: asset?.asset_library_item_id ? String(asset.asset_library_item_id) : undefined,
      media_url: mediaUrl,
      placeholder_behavior: mediaUrl ? undefined : "hide if unavailable",
    },
    placement,
    opacity,
    rotation: 0,
    editable: true,
    animation,
    custom: {
      enrichment_source: "scene_recipe",
      recipe_id: recipeId,
    },
  };
}

function classifyScene(scene = {}, index = 0, recipeMap = {}) {
  const key = normalizeRole(`${scene.scene_id || ""}_${scene.name || ""}`);
  const explicitRecipe =
    recipeMap[scene.scene_id] ||
    recipeMap[scene.page_id] ||
    recipeMap[normalizeRole(scene.scene_id)] ||
    recipeMap[normalizeRole(scene.page_id)];
  if (explicitRecipe) return explicitRecipe;

  if (index === 0 || key.includes("opening") || key.includes("hero")) return "opening";
  if (key.includes("program") || key.includes("schedule") || key.includes("details")) return "program_schedule_card";
  if (key.includes("venue") || key.includes("rsvp")) return "venue_rsvp_card";
  if (key.includes("closing") || key.includes("blessing")) return "closing";
  return "readable_detail_card";
}

function getSceneKey(scene = {}, fallback = "scene") {
  return normalizeRole(scene.scene_id || scene.page_id || scene.name || fallback);
}

function isTextElement(element = {}) {
  return String(element.type || "").toLowerCase() === "text";
}

function isDetailRecipe(recipeId = "") {
  return ["readable_detail_card", "program_schedule_card", "venue_rsvp_card"].includes(recipeId);
}

function getDetailTextRole(element = {}) {
  const role = getElementRole(element);
  if (role.includes("title") || role.includes("heading")) return "title";
  if (role.includes("kicker") || role.includes("label")) return "kicker";
  return "details";
}

function isReadableOnLightPanel(fill = "") {
  const value = String(fill || "").trim().toLowerCase();
  if (!value) return false;
  if (value === "black") return true;
  if (value === "white" || value === "#fff" || value === "#ffffff") return false;

  const hex = value.match(/^#([0-9a-f]{6})$/i);
  if (!hex) {
    return !value.includes("255") && !value.includes("fff");
  }

  const raw = hex[1];
  const red = parseInt(raw.slice(0, 2), 16);
  const green = parseInt(raw.slice(2, 4), 16);
  const blue = parseInt(raw.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance < 0.62;
}

function enrichDetailTextElement(element, { index, width, height, recipeId }) {
  if (!isTextElement(element) || !isDetailRecipe(recipeId)) {
    return element;
  }

  const role = getDetailTextRole(element);
  const panelX = Math.round(width * 0.1);
  const panelY = Math.round(height * 0.25);
  const panelWidth = Math.round(width * 0.8);
  const innerX = panelX + Math.round(width * 0.08);
  const innerWidth = panelWidth - Math.round(width * 0.16);
  const titleY = panelY + Math.round(height * 0.1);
  const detailsY = panelY + Math.round(height * 0.23) + index * Math.round(height * 0.015);
  const existingText = element.text || {};

  if (role === "title") {
    return {
      ...element,
      layer_index: Math.max(Number(element.layer_index) || 0, 6),
      placement: {
        ...(element.placement || {}),
        x: innerX,
        y: titleY,
        width: innerWidth,
        height: Math.max(Number(element.placement?.height) || 130, 110),
        fit: "none",
        safe_area_role: "inside_readable_panel",
      },
      text: {
        ...existingText,
        font_size: Math.min(Number(existingText.font_size) || 58, 64),
        font_weight: existingText.font_weight || "700",
        fill: isReadableOnLightPanel(existingText.fill) ? existingText.fill : "#7c2d12",
        align: existingText.align || "center",
        line_height: existingText.line_height || 1.12,
        letter_spacing: existingText.letter_spacing ?? 0.2,
      },
      animation: element.animation?.length ? element.animation : motionPresets.detailTextReveal(450),
    };
  }

  return {
    ...element,
    layer_index: Math.max(Number(element.layer_index) || 0, 6),
    placement: {
      ...(element.placement || {}),
      x: innerX,
      y: detailsY,
      width: innerWidth,
      height: Math.max(Number(element.placement?.height) || 280, role === "kicker" ? 90 : 250),
      fit: "none",
      safe_area_role: "inside_readable_panel",
    },
    text: {
      ...existingText,
      font_size: Math.min(Number(existingText.font_size) || (role === "kicker" ? 36 : 42), role === "kicker" ? 40 : 46),
      font_weight: existingText.font_weight || (role === "kicker" ? "600" : "500"),
      fill: isReadableOnLightPanel(existingText.fill) ? existingText.fill : "#4a2412",
      align: existingText.align || "center",
      line_height: existingText.line_height || 1.22,
      letter_spacing: existingText.letter_spacing ?? 0.1,
    },
    animation: element.animation?.length ? element.animation : motionPresets.detailTextReveal(700 + index * 120),
  };
}

function readableDetailCardRecipe({ scene, selectedAssets, width, height, recipeId = "readable_detail_card" }) {
  const elements = [...(scene.elements || [])];
  const additions = [];
  const sceneKey = getSceneKey(scene, recipeId);
  const panelX = Math.round(width * 0.1);
  const panelY = Math.round(height * 0.25);
  const panelWidth = Math.round(width * 0.8);
  const panelHeight = Math.round(height * 0.5);

  if (!hasRoleLike(elements, ["panel_shadow", "inner_border", "top_rule", "bottom_glow"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-panel-shadow`,
        role: "readable_panel_shadow",
        recipeId,
        layerIndex: 0.8,
        placement: {
          x: panelX + 18,
          y: panelY + 22,
          width: panelWidth,
          height: panelHeight,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(43, 12, 6, 0.28)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 46,
        opacity: 1,
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-readable-panel`,
        role: "readable_details_card",
        recipeId,
        layerIndex: 1,
        placement: {
          x: panelX,
          y: panelY,
          width: panelWidth,
          height: panelHeight,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 243, 209, 0.9)",
        stroke: "rgba(212, 175, 55, 0.6)",
        strokeWidth: 3,
        cornerRadius: 42,
        animation: motionPresets.detailPanelReveal(100),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-panel-inner-border`,
        role: "readable_panel_inner_border",
        recipeId,
        layerIndex: 1.2,
        placement: {
          x: panelX + 28,
          y: panelY + 28,
          width: panelWidth - 56,
          height: panelHeight - 56,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255,255,255,0)",
        stroke: "rgba(212, 175, 55, 0.36)",
        strokeWidth: 2,
        cornerRadius: 30,
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-panel-top-rule`,
        role: "readable_panel_top_rule",
        recipeId,
        layerIndex: 1.4,
        placement: {
          x: panelX + Math.round(panelWidth * 0.17),
          y: panelY + 84,
          width: Math.round(panelWidth * 0.66),
          height: 4,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.62)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 8,
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-panel-bottom-glow`,
        role: "readable_panel_bottom_glow",
        recipeId,
        layerIndex: 1.4,
        placement: {
          x: panelX + Math.round(panelWidth * 0.14),
          y: panelY + panelHeight - 115,
          width: Math.round(panelWidth * 0.72),
          height: 74,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.12)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 70,
      })
    );
  }

  if (!hasRoleLike(elements, ["divider"])) {
    const dividerAsset = findSelectedAssetByRole(selectedAssets, ["divider", "lotus"]);
    additions.push(
      dividerAsset
        ? buildRecipeAsset({
            id: `${sceneKey}-recipe-divider`,
            role: "lotus_divider",
            recipeId,
            asset: dividerAsset,
            layerIndex: 4,
            placement: {
              x: Math.round(width * 0.28),
              y: Math.round(height * 0.41),
              width: Math.round(width * 0.44),
              height: 70,
              fit: "contain",
              safe_area_role: "inside_safe_area",
            },
            opacity: 0.9,
            animation: motionPresets.dividerReveal(550),
          })
        : buildRecipeFigure({
            id: `${sceneKey}-recipe-divider`,
            role: "gold_divider",
            recipeId,
            layerIndex: 4,
            placement: {
              x: Math.round(width * 0.28),
              y: Math.round(height * 0.43),
              width: Math.round(width * 0.44),
              height: 5,
              fit: "none",
              safe_area_role: "inside_safe_area",
            },
            fill: "rgba(212, 175, 55, 0.88)",
          })
    );
  }

  const watermarkAsset = findSelectedAssetByRole(selectedAssets, ["ganesha", "ganpati", "hero", "icon"]);
  if (watermarkAsset && !hasRoleLike(elements, ["watermark"])) {
    additions.push(
      buildRecipeAsset({
        id: `${sceneKey}-recipe-watermark`,
        role: "subtle_watermark",
        recipeId,
        asset: watermarkAsset,
        layerIndex: 2,
        placement: {
          x: Math.round(width * 0.35),
          y: Math.round(height * 0.31),
          width: Math.round(width * 0.3),
          height: Math.round(width * 0.3),
          fit: "contain",
          safe_area_role: "inside_safe_area",
        },
        opacity: 0.08,
        animation: motionPresets.detailOrnamentReveal(700),
      })
    );
  }

  if (!hasRoleLike(elements, ["corner", "ornament"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-corner-top-left`,
        role: "corner_ornament",
        recipeId,
        layerIndex: 3,
        placement: { x: 92, y: 420, width: 135, height: 135, fit: "none", safe_area_role: "inside_safe_area" },
        fill: "rgba(212, 175, 55, 0.22)",
        stroke: "rgba(212, 175, 55, 0.42)",
        strokeWidth: 2,
        subType: "circle",
        animation: motionPresets.detailOrnamentReveal(500),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-corner-bottom-right`,
        role: "corner_ornament",
        recipeId,
        layerIndex: 3,
        placement: {
          x: width - 227,
          y: Math.round(height * 0.71),
          width: 135,
          height: 135,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.18)",
        stroke: "rgba(212, 175, 55, 0.38)",
        strokeWidth: 2,
        subType: "circle",
        animation: motionPresets.detailOrnamentReveal(650),
      })
    );
  }

  if (!hasRoleLike(elements, ["side_accent", "accent_bar"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-left-accent-bar`,
        role: "side_accent_bar",
        recipeId,
        layerIndex: 2.5,
        placement: {
          x: panelX - 8,
          y: panelY + 90,
          width: 8,
          height: panelHeight - 180,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.72)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 8,
        animation: motionPresets.dividerReveal(500),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-right-accent-bar`,
        role: "side_accent_bar",
        recipeId,
        layerIndex: 2.5,
        placement: {
          x: panelX + panelWidth,
          y: panelY + 90,
          width: 8,
          height: panelHeight - 180,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.52)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 8,
        animation: motionPresets.dividerReveal(620),
      })
    );
  }

  return additions;
}

function devotionalOpeningRecipe({ scene, selectedAssets = [], width, height, recipeId = "devotional_opening" }) {
  const elements = [...(scene.elements || [])];
  const additions = [];
  const sceneKey = getSceneKey(scene, recipeId);

  if (!hasRoleLike(elements, ["vignette", "stage_depth"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-stage-vignette`,
        role: "stage_depth_vignette",
        recipeId,
        layerIndex: 1,
        placement: {
          x: Math.round(width * 0.05),
          y: Math.round(height * 0.08),
          width: Math.round(width * 0.9),
          height: Math.round(height * 0.82),
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(74, 18, 8, 0.22)",
        stroke: "rgba(212, 175, 55, 0.18)",
        strokeWidth: 4,
        cornerRadius: 90,
      })
    );
  }

  if (!hasRoleLike(elements, ["premium_frame", "side_rail", "temple_border"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-left-side-rail`,
        role: "premium_side_rail",
        recipeId,
        layerIndex: 1.6,
        placement: {
          x: Math.round(width * 0.045),
          y: Math.round(height * 0.12),
          width: 8,
          height: Math.round(height * 0.72),
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.58)",
        stroke: "rgba(255, 232, 163, 0.26)",
        strokeWidth: 2,
        cornerRadius: 8,
        animation: motionPresets.dividerReveal(450),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-right-side-rail`,
        role: "premium_side_rail",
        recipeId,
        layerIndex: 1.6,
        placement: {
          x: Math.round(width * 0.947),
          y: Math.round(height * 0.12),
          width: 8,
          height: Math.round(height * 0.72),
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.58)",
        stroke: "rgba(255, 232, 163, 0.26)",
        strokeWidth: 2,
        cornerRadius: 8,
        animation: motionPresets.dividerReveal(450),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-top-temple-rule`,
        role: "premium_temple_border",
        recipeId,
        layerIndex: 1.7,
        placement: {
          x: Math.round(width * 0.18),
          y: Math.round(height * 0.145),
          width: Math.round(width * 0.64),
          height: 5,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 232, 163, 0.64)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 8,
        animation: motionPresets.dividerReveal(600),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-bottom-temple-rule`,
        role: "premium_temple_border",
        recipeId,
        layerIndex: 1.7,
        placement: {
          x: Math.round(width * 0.24),
          y: Math.round(height * 0.82),
          width: Math.round(width * 0.52),
          height: 5,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 232, 163, 0.48)",
        stroke: "rgba(0,0,0,0)",
        cornerRadius: 8,
        animation: motionPresets.dividerReveal(900),
      })
    );
  }

  if (!hasRoleLike(elements, ["halo", "glow"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-halo`,
        role: "hero_halo_glow",
        recipeId,
        layerIndex: 2,
        placement: {
          x: Math.round(width * 0.2),
          y: Math.round(height * 0.18),
          width: Math.round(width * 0.6),
          height: Math.round(width * 0.6),
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 214, 102, 0.18)",
        stroke: "rgba(255, 224, 138, 0.24)",
        strokeWidth: 6,
        subType: "circle",
        animation: motionPresets.heroReveal(300),
      })
    );
  }

  const garlandAsset = findSelectedAssetByRole(selectedAssets, [
    "marigold_top_garland",
    "marigold_top",
    "marigold_frame",
    "marigold",
    "garland",
    "floral",
  ]);
  if (garlandAsset && !hasRoleLike(elements, ["top_garland", "marigold_top"])) {
    additions.push(
      buildRecipeAsset({
        id: `${sceneKey}-recipe-top-garland`,
        role: "marigold_top_garland",
        recipeId,
        asset: garlandAsset,
        layerIndex: 8,
        placement: {
          x: Math.round(width * -0.02),
          y: Math.round(height * 0.02),
          width: Math.round(width * 1.04),
          height: Math.round(height * 0.26),
          fit: "contain",
          safe_area_role: "top_frame",
        },
        opacity: 1,
        animation: motionPresets.garlandDrop(150),
      })
    );
  }

  if (!hasRoleLike(elements, ["floor_glow", "base_glow"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-floor-glow`,
        role: "floor_glow",
        recipeId,
        layerIndex: 2.2,
        placement: {
          x: Math.round(width * 0.18),
          y: Math.round(height * 0.66),
          width: Math.round(width * 0.64),
          height: Math.round(height * 0.16),
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 184, 77, 0.18)",
        stroke: "rgba(255, 224, 138, 0.16)",
        strokeWidth: 3,
        cornerRadius: 120,
        subType: "circle",
        animation: motionPresets.floorGlowPulse(0),
      })
    );
  }

  if (!hasRoleLike(elements, ["particle", "sparkle"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-top-sparkle`,
        role: "soft_particle_overlay",
        recipeId,
        layerIndex: 8,
        placement: {
          x: Math.round(width * 0.16),
          y: Math.round(height * 0.11),
          width: 34,
          height: 34,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 232, 163, 0.58)",
        subType: "circle",
        animation: motionPresets.softSparkle(900),
      }),
      buildRecipeFigure({
        id: `${sceneKey}-recipe-bottom-sparkle`,
        role: "soft_particle_overlay",
        recipeId,
        layerIndex: 8,
        placement: {
          x: Math.round(width * 0.78),
          y: Math.round(height * 0.74),
          width: 42,
          height: 42,
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(255, 232, 163, 0.46)",
        subType: "circle",
        animation: motionPresets.softSparkle(1200),
      })
    );
  }

  return additions;
}

function closingBlessingRecipe({ scene, selectedAssets, width, height }) {
  const additions = devotionalOpeningRecipe({ scene, selectedAssets, width, height, recipeId: "closing_blessing" });
  const elements = [...(scene.elements || [])];
  const sceneKey = getSceneKey(scene, "closing_blessing");
  const sideAsset = findSelectedAssetByRole(selectedAssets, ["marigold_side", "garland", "floral"]);

  if (sideAsset && !hasRoleLike(elements, ["frame", "garland"])) {
    additions.push(
      buildRecipeAsset({
        id: `${sceneKey}-recipe-closing-frame`,
        role: "settled_floral_frame",
        recipeId: "closing_blessing",
        asset: sideAsset,
        layerIndex: 3,
        placement: {
          x: Math.round(width * 0.08),
          y: Math.round(height * 0.08),
          width: Math.round(width * 0.84),
          height: Math.round(height * 0.18),
          fit: "contain",
          safe_area_role: "inside_safe_area",
        },
        opacity: 0.62,
        animation: motionPresets.settledFrameReveal(350),
      })
    );
  }

  return additions;
}

function programScheduleRecipe({ scene, selectedAssets, width, height }) {
  const additions = readableDetailCardRecipe({
    scene,
    selectedAssets,
    width,
    height,
    recipeId: "program_schedule_card",
  });
  const elements = [...(scene.elements || [])];
  const sceneKey = getSceneKey(scene, "program_schedule_card");

  if (!hasRoleLike(elements, ["badge", "program_icon"])) {
    additions.push(
      buildRecipeFigure({
        id: `${sceneKey}-recipe-program-badge`,
        role: "program_icon_badge",
        recipeId: "program_schedule_card",
        layerIndex: 5,
        placement: {
          x: Math.round(width * 0.42),
          y: Math.round(height * 0.22),
          width: Math.round(width * 0.16),
          height: Math.round(width * 0.16),
          fit: "none",
          safe_area_role: "inside_safe_area",
        },
        fill: "rgba(212, 175, 55, 0.22)",
        stroke: "rgba(212, 175, 55, 0.62)",
        strokeWidth: 3,
        subType: "circle",
        animation: motionPresets.programBadgePop(450),
      })
    );
  }

  return additions;
}

function venueRsvpRecipe({ scene, selectedAssets, width, height }) {
  const additions = readableDetailCardRecipe({
    scene,
    selectedAssets,
    width,
    height,
    recipeId: "venue_rsvp_card",
  });
  const elements = [...(scene.elements || [])];
  const sceneKey = getSceneKey(scene, "venue_rsvp_card");

  if (!hasRoleLike(elements, ["floor", "rangoli"])) {
    const rangoliAsset = findSelectedAssetByRole(selectedAssets, ["rangoli", "floor"]);
    if (rangoliAsset) {
      additions.push(
        buildRecipeAsset({
          id: `${sceneKey}-recipe-rangoli-floor`,
          role: "rangoli_floor_accent",
          recipeId: "venue_rsvp_card",
          asset: rangoliAsset,
          layerIndex: 2,
          placement: {
            x: Math.round(width * 0.26),
            y: Math.round(height * 0.77),
            width: Math.round(width * 0.48),
            height: Math.round(height * 0.14),
            fit: "contain",
            safe_area_role: "bottom_accent",
          },
          opacity: 0.46,
        })
      );
    }
  }

  return additions;
}

function getRecipeAdditions({ scene, sceneType, selectedAssets, width, height }) {
  if (sceneType === "opening" || sceneType === "hero_opening" || sceneType === "devotional_opening") {
    return devotionalOpeningRecipe({ scene, selectedAssets, width, height });
  }
  if (sceneType === "closing" || sceneType === "closing_blessing") {
    return closingBlessingRecipe({ scene, selectedAssets, width, height });
  }
  if (sceneType === "program_schedule_card") {
    return programScheduleRecipe({ scene, selectedAssets, width, height });
  }
  if (sceneType === "venue_rsvp_card") {
    return venueRsvpRecipe({ scene, selectedAssets, width, height });
  }
  return readableDetailCardRecipe({ scene, selectedAssets, width, height });
}

export function applySceneRecipes(compositionPlan, selectedAssets = [], family = {}) {
  const canvas = compositionPlan?.canvas || {};
  const width = canvas.width || 1080;
  const height = canvas.height || 1920;
  const recipeMap = getSceneRecipeMapForFamily(family);

  return {
    ...compositionPlan,
    custom: {
      ...(compositionPlan?.custom || {}),
      scene_recipes_enabled: true,
    },
    scenes: (compositionPlan?.scenes || []).map((scene, index) => {
      const sceneType = classifyScene(scene, index, recipeMap);
      const recipeMetadata = getRecipeMetadata(sceneType);
      const additions = getRecipeAdditions({ scene, sceneType, selectedAssets, width, height });
      const enrichedExistingElements = (scene.elements || []).map((element, elementIndex) =>
        enrichDetailTextElement(element, {
          index: elementIndex,
          width,
          height,
          recipeId: sceneType,
        })
      );

      return {
        ...scene,
        elements: [...additions, ...enrichedExistingElements],
        custom: {
          ...(scene.custom || {}),
          applied_scene_recipe: sceneType,
          scene_recipe_purpose: recipeMetadata?.purpose || "",
          scene_recipe_minimum_layers: recipeMetadata?.minimumLayers || null,
          scene_recipe_required_roles: recipeMetadata?.requiredRoles || [],
          scene_recipe_recommended_roles: recipeMetadata?.recommendedRoles || [],
          scene_recipe_additions: additions.map((element) => element.role),
        },
      };
    }),
  };
}
